import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { BaryResult, BaryResultBuilder } from '../../../../models/bary-result';
import { NgxSpinnerService } from 'ngx-spinner';
import { FileUploader, FileUploadModule } from 'ng2-file-upload';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaryComponentHelper } from '../../util/bary-component-helper';
import { FormControl } from '@angular/forms';
import { BaryUploadUrlEnum } from '../../../../models/enums/bary-upload-url.enum';
import { ALLOWED_IMG_FORMAT, DEFAULT_SIZE, ONE_MB } from '../../../../models/constants/file-upload.const';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';
import { BaryIconComponent } from '../bary-icon/bary-icon.component';
import { MDBBootstrapModulesPro } from 'ng-uikit-pro-standard';
import { NgIf } from '@angular/common';
import { BaryButtonComponent } from '../bary-button/bary-button.component';

@Component({
  selector: 'jhi-bary-file-upload-visual',
  templateUrl: './bary-file-upload-visual.component.html',
  styleUrls: ['./bary-file-upload-visual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [BaryIconComponent, MDBBootstrapModulesPro, TranslateModule, NgIf, FileUploadModule, BaryButtonComponent],
})
export class BaryFileUploadVisualComponent implements OnInit, OnChanges {
  private readonly localStorage = inject(LocalStorageService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly translateService = inject(TranslateService);

  @Input() label?: string;
  @Input() actionURL?: string | BaryUploadUrlEnum; // call direct en ajax
  @Input() existingUrl?: string;
  @Input() control: FormControl = new FormControl('');
  @Input() inProcessing = false;
  @Input() allowedFileType: string[] = ALLOWED_IMG_FORMAT;
  @Input() maxFileSize = ONE_MB;
  @Input() maxHeight = DEFAULT_SIZE.maxHeight;
  @Input() maxWidth = DEFAULT_SIZE.maxWidth;
  @Input() preview = false;
  @Input() id = '';
  @Input() downloadUrl?: string;
  @Input() downloadEmpty?: string;
  @Input() previewControl: FormControl = new FormControl('');
  @Input() icon = 'user';
  @Input() displayCircle = true;

  @Input() set inRefreshing(value: boolean) {
    if (value) {
      this.spinner.show('refreshing').then();
    } else {
      this.spinner.hide('refreshing').then();
    }
    this._refreshing = value;
  }

  @Output() resultEvent = new EventEmitter<BaryResult<any>>();
  @Output() refreshEvent = new EventEmitter<undefined>();

  @ViewChild('uploadFileVisual') uploadFile!: ElementRef;

  public previewSrc = '';
  public _errors: string[] = [];

  public uploader!: FileUploader;

  public _style?: string;

  private reader?: FileReader;
  private file?: File;

  private _refreshing = false;
  private _allowedMimeTypes = ['image/jpg', 'image/png', 'image/jpeg'];
  private _imageMimeTypes = ['image/jpg', 'image/png', 'image/jpeg'];

  protected readonly FAS = FaDirectiveEnum.FAS;

  get inRefreshing(): boolean {
    return this._refreshing;
  }

  public ngOnInit(): void {
    if (!this.id) {
      this.id = BaryComponentHelper.makeId();
    }

    if (this.allowedFileType.length === 0) {
      this.allowedFileType = this._allowedMimeTypes;
    }

    this.refreshStyle();
    this.previewControl.valueChanges.subscribe(() => {
      this.refreshStyle();
    });

    this.initFileUpload();
  }

  public forceUpload(): void {
    this.uploader.options.url = this.actionURL;
    this.uploader.uploadAll();
  }

  public checkFile(files: File[]): void {
    this._errors = [];
    this.previewSrc = '';
    this.file = files[0];

    if (!this.file || !this.isAllowedFile(this.file)) {
      this.addError(
        this.translateService.instant('bary-file-upload.error.invalidFormat', {
          formatList: this.allowedFileType.toString(),
        })
      );
      return;
    }

    if (this.file.size > this.maxFileSize) {
      this.addError(
        this.translateService.instant('bary-file-upload.error.invalidWeight', {
          maxWeight: (this.maxFileSize / 1048576).toFixed(1),
        })
      );
      return;
    }

    this.reader = new FileReader();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (this.file && this.isAllowedFile(this.file)) {
      this.control.setValue(this.file.name);
      if (this.isAllowedFile(this.file)) {
        this.reader.onload = (e: any) => {
          if (this.isImageFile(this.file!)) {
            this.processAsImageWithVisual(e);
          } else {
            this.processAsNotImageFile(e);
          }
        };
        this.reader.readAsDataURL(this.file);
      }
    }
  }

  private isAllowedFile(file: File): boolean {
    return this.allowedFileType.includes(file.type);
  }

  private isImageFile(file: File): boolean {
    return this._imageMimeTypes.includes(file.type);
  }

  private initFileUpload(): void {
    if (this.actionURL) {
      const token = this.localStorage.retrieve('authenticationToken') || this.sessionStorage.retrieve('authenticationToken');

      this.uploader = new FileUploader({
        url: this.actionURL,
        itemAlias: 'uploadedFile',
        authToken: 'Bearer ' + token,
        maxFileSize: this.maxFileSize,
      });

      this.uploader.onBeforeUploadItem = (item: any) => {
        this.spinner.show('fileProcessing').then();
        item.withCredentials = false;
      };
      this.uploader.onAfterAddingFile = file => {
        file.withCredentials = false;
      };
      this.uploader.onCompleteItem = (item: any, response: any, status: any) => {
        const parsedResponse = new BaryResultBuilder()
          .parseResponse(response)
          .success(status === 200)
          .build();

        this.resultEvent.emit(parsedResponse);
        this.spinner.hide('fileProcessing').then();
        this.uploader.clearQueue();
        this.uploadFile.nativeElement.value = ''; // reset l'affichage.
      };
    }
  }

  private addError(error: string): void {
    this._errors.push(error);
    setTimeout(() => {
      this._errors = [];
      this.uploadFile.nativeElement.value = '';
      this.uploader.clearQueue();
    }, 5000);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (changes['inProcessing'] && changes['inProcessing'].currentValue === true) {
      this.control.disable({ onlySelf: true, emitEvent: false });
    } else {
      this.control.enable({ onlySelf: true, emitEvent: false });
    }
  }

  public download(url: string): void {
    window.open(url, '_blank');
  }

  public deletePicture(): void {
    this.control.setValue(undefined);
    this.control.markAsDirty();
    this.previewSrc = '';
    this.previewControl.setValue(undefined);
    this.previewControl.markAsDirty();
    this.existingUrl = '';
    this.refreshStyle();
  }

  private refreshStyle(): void {
    if (this.previewSrc) {
      this._style = `background-image: url('${this.previewSrc}'); background-size: 100%; background-repeat: no-repeat;`;
    } else if (this.existingUrl) {
      this._style = `background-image: url('${this.existingUrl}'); background-size: 100%; background-repeat: no-repeat;`;
    } else {
      this._style = 'background-image: none';
    }
  }

  private processAsImageWithVisual(e: any): void {
    const image = new Image();
    image.src = e.target.result;
    image.onload = (rs: any) => {
      const target = rs.currentTarget;
      if (target) {
        const img_height = target['height'] as number;
        const img_width = target['width'] as number;
        if (img_height > this.maxHeight || img_width > this.maxWidth) {
          this.addError(
            this.translateService.instant('bary-file-upload.error.invalidImageDimension', {
              maxWidth: this.maxWidth,
              maxHeight: this.maxHeight,
            })
          );
          this.previewSrc = '';
          this.control.reset();
        } else {
          this.previewSrc = image.src;
          this.previewControl.setValue(image.src);
          this.previewControl.markAsDirty();
          this.previewControl.markAsTouched();
        }
      }
    };
  }

  private processAsNotImageFile(e: any): void {
    this.previewSrc = 'content/images/big_pdf.png';
    this.previewControl.setValue(e.target.result);
    this.previewControl.markAsDirty();
    this.previewControl.markAsTouched();
  }
}
